// src/components/Layout/AppLayout.tsx - WITH ONBOARDING GUARD
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import { MarketingHeader } from '@/components/marketing/MarketingHeader';
import SmartSidebar from '@/components/navigation/SmartSidebar';
import FloatingSmartNav from '@/components/navigation/FloatingSmartNav';
import { ScrollToTop } from '@/components/ScrollToTop';
import { ScrollProgress } from '@/components/ScrollProgress';
import { PageGuide } from '@/components/guide/PageGuide';
import { getUprmLockStatus, wizardRouteFor } from '@/services/UprmLock';

interface AppLayoutProps {
  children: React.ReactNode;
}

const getSessionUser = () => {
  const data = localStorage.getItem('auth_user');
  return data ? JSON.parse(data) : { name: "Commander", role: "admin" };
};

// Disabled 2026-09-06 at the founder's request: this raw internal route
// browser (627 routes across 13 sections, e.g. "core.ai_chat", "core.tier_admin")
// was visible to real logged-in users on every non-marketing page. Left in
// place, not deleted — flip back to true to restore it.
const SHOW_SMART_SIDEBAR = false;

// Disabled 2026-09-12 at the founder's request: this fixed bottom-right ("?")
// trigger renders on every non-marketing page at z-[60], including on top of
// each page's own bottom-right controls -- reported concretely on Global
// Connect, where it sat over the chat composer / system buttons. Left in
// place, not deleted — flip back to true to restore it.
const SHOW_PAGE_GUIDE = false;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [showTraditionalSidebar, setShowTraditionalSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userFromStorage = getSessionUser();

  // Public marketing pages — the internal tier/admin shell (SmartSidebar,
  // the Header's Mission Control / Power Tap / tier-OS nav bar, PageGuide)
  // is for logged-in operators, not visitors. Most of this list mirrors the
  // "1. Landing & Marketing (ALL public — protected: false)" section of
  // src/routes/domains/core/core.routes.tsx exactly (plus the root path,
  // which renders HomePage outside that labeled block) — keep the two in
  // sync if that section's paths change. A few entries (e.g. /uk-immigration-hub,
  // registered in uk.routes.tsx) are public marketing pages that live outside
  // that block; add others here as they turn up missing their header.
  const isMarketingPage = [
    '/',
    '/homepage',
    '/how-it-works',
    '/canada-immigration',
    '/uk-immigration',
    '/uk-immigration-hub',
    '/countries',
    '/about',
    '/why-us',
    '/pricing',
    '/contact',
    '/team',
    '/privacy',
    '/cookies',
    '/accessibility',
    '/blog',
    '/australia-immigration',
    '/germany-immigration',
    '/new-zealand-immigration',
    '/ireland-immigration',
    '/features',
    '/system-map',
    '/clearid',
  ].includes(location.pathname)
    // Each article's own page (/blog/:slug, BlogArticlePage.tsx) is just as
    // public as the /blog listing itself -- prefix-matched since these are
    // dynamic slugs, not a fixed set of paths the array above can list.
    || location.pathname.startsWith('/blog/');
  // Kept as an alias so the rest of this file (padding/margin helpers,
  // sidebar/guide gating) doesn't need a second rename pass.
  const isDocumentationPage = isMarketingPage;

  // Full-screen chat experiences (Global Connect, all three route aliases --
  // see the routing comment at the top of GlobalConnect.tsx) want to look
  // and behave like a native messaging app (Messenger/WhatsApp) on mobile,
  // not like a widget embedded in the OS dashboard shell: no rounded card
  // inset, no OS-switcher bar competing with its own tab bar. Desktop keeps
  // the normal windowed/card treatment -- this was reported as a mobile-only
  // problem (screenshots compared against Messenger/WhatsApp on phone).
  const isImmersiveChatPage = [
    '/global-connect',
    '/global-connect-messages',
    '/global-messenger',
  ].includes(location.pathname);

  // ✅ ONBOARDING ROUTE GUARD
  useEffect(() => {
    if (isDocumentationPage) return;
    if (user && !user.is_onboarding_completed && location.pathname !== '/onboarding') {
      navigate('/onboarding');
    }
  }, [user, location.pathname, navigate, isDocumentationPage]);

  // ✅ First-wizard lock — UPRM stays closed until assessment wizard completes
  useEffect(() => {
    if (isDocumentationPage || !user?.id || !user.is_onboarding_completed) return;
    const path = location.pathname;
    const exempt =
      path.startsWith('/journey-launch-inside') ||
      path.startsWith('/journey-entrance') ||
      path.startsWith('/journey-launch') ||
      path.startsWith('/assessment') ||
      path.startsWith('/uk-cos-companion') ||
      path.startsWith('/companion-results') ||
      path.startsWith('/canada-pr-companion') ||
      path.startsWith('/canada-companion-results') ||
      path.startsWith('/germany-skilled-companion') ||
      path.startsWith('/germany-companion-results') ||
      path.startsWith('/nz-skilled-companion') ||
      path.startsWith('/nz-companion-results') ||
      path.startsWith('/ireland-skills-companion') ||
      path.startsWith('/ireland-companion-results') ||
      path.startsWith('/onboarding') ||
      path.startsWith('/settings');
    if (exempt) return;
    if (path !== '/journey' && !path.startsWith('/journey/')) return;

    let cancelled = false;
    void getUprmLockStatus(user.id).then((status) => {
      if (!cancelled && status.locked) {
        navigate(wizardRouteFor(status.residency), { replace: true });
      }
    });
    return () => { cancelled = true; };
  }, [user?.id, user?.is_onboarding_completed, location.pathname, navigate, isDocumentationPage]);

  // Check mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleNavMode = () => {
    setShowTraditionalSidebar(!showTraditionalSidebar);
  };

  // Responsive padding based on device and page type
  const getResponsivePadding = () => {
    if (isDocumentationPage || isImmersiveChatPage) return '0';
    if (isMobile) return '12px';
    return '24px';
  };

  const getResponsiveMargin = () => {
    if (isDocumentationPage || isImmersiveChatPage) return '0';
    if (isMobile) return '0';
    return '12px 16px 16px 16px';
  };

  const getResponsiveBorderRadius = () => {
    if (isDocumentationPage || isImmersiveChatPage) return '0';
    if (isMobile) return '12px';
    return '20px';
  };

  return (
    <>
      <ScrollToTop containerRef={mainRef} />
      <ScrollProgress containerRef={mainRef} />
    <div className="min-h-screen flex bg-[#020617] text-slate-50 overflow-hidden w-full">
      
      {/* TRADITIONAL SIDEBAR */}
      {SHOW_SMART_SIDEBAR && !isDocumentationPage && showTraditionalSidebar && !isMobile && (
        <div className="shrink-0 relative z-30 shadow-2xl border-r border-white/5" style={{ width: '260px' }}>
          <SmartSidebar />
        </div>
      )}

      {/* MAIN CONTAINER — h-dvh, not h-screen: mobile Safari's 100vh is the
          "large viewport" (chrome hidden), taller than what's actually
          visible when the address bar/toolbar are showing. Sizing this
          exactly to 100vh made it overflow the real visible area on phones,
          which is what let the page scroll as a whole and drag fixed-bottom
          content (Global Connect's tab bar) down past the visible fold. */}
      <div className="flex-1 min-w-0 flex flex-col relative z-0 h-dvh overflow-hidden w-full">
        
        {/* HEADER CONTAINER — the internal tier/admin nav bar (Mission Control,
            Power Tap, per-OS shortcuts) has no place on a public marketing
            page, so marketing routes get the shared MarketingHeader instead.
            Every marketing page except HomePage.tsx (which still renders its
            own copy inline) relies on this one. */}
        {/* Immersive messenger (Global Connect): no OS Header / VISA GUIDE OS lockup —
            chat is full-bleed like WhatsApp Web. */}
        {!isImmersiveChatPage && (
          <div className="w-full flex-shrink-0 relative z-20">
            {isMarketingPage ? (
              <MarketingHeader />
            ) : (
              <Header
                onNavModeToggle={toggleNavMode}
                currentNavMode={showTraditionalSidebar ? 'traditional' : 'floating'}
              />
            )}
          </div>
        )}

        {/* SCROLLABLE CONTENT AREA */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 w-full">
          <main
            ref={mainRef}
            className="flex-1 overflow-y-auto custom-scrollbar w-full"
            style={{
              padding: getResponsivePadding(),
              background: isDocumentationPage || isImmersiveChatPage ? 'transparent' : 'rgba(15, 23, 42, 0.2)',
              // backdrop-filter on an ancestor becomes the containing block for any
              // position:fixed descendant (CSS spec) -- disabling it for the immersive
              // chat page keeps its own fixed panels (bottom nav, modals) anchored to
              // the real screen instead of this box. See the h-full fix in
              // GlobalConnect.tsx for the other half of that same bug.
              backdropFilter: isDocumentationPage || isImmersiveChatPage ? 'none' : 'blur(8px)',
              margin: getResponsiveMargin(),
              borderRadius: getResponsiveBorderRadius(),
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            {children}
          </main>
        </div>
      </div>

      {/* FLOATING COMPANIONS
          FloatingChatbot was removed: it answered immigration questions with a
          RANDOM canned string (invented processing times, fabricated funding
          requirements). PageGuide replaces it — authored truth + constrained AI. */}
      {SHOW_PAGE_GUIDE && !isDocumentationPage && !isImmersiveChatPage && <PageGuide />}

      {/* CSS styles (unchanged) */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.45); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.6); }
        body { 
          overflow: hidden !important;
          margin: 0;
          padding: 0;
        }
        header, .header {
          width: 100%;
          box-sizing: border-box;
        }
        *, *::before, *::after {
          box-sizing: border-box;
        }
        html, body, #root {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          margin: 0;
          padding: 0;
        }
        .flex-1 {
          min-width: 0;
          width: 100%;
        }
        @media (max-width: 768px) {
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          main {
            width: calc(100% - 0px) !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }
        }
        @media (min-width: 769px) {
          main {
            width: 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default AppLayout;